import React, { FormEvent, useEffect, useState } from "react";
import {
	Button,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { Dispatch } from "redux";
import { showAlert } from "../../common/functions";
import { VideoHotlineCategoryAction } from "../../../actions/videoHotlineCategoryAction";

interface ManageCategoryModalProps {
	isOpen: boolean;
	toggleDetailModal: () => void;
	id: number;
	dispatch: Dispatch<any>;
	videoHotlineCategoryData: any;
	detailLoading: any;
}

const VideoHotlineCategoryModal: React.FC<ManageCategoryModalProps> = ({
	isOpen = false,
	toggleDetailModal,
	id,
	dispatch,
	videoHotlineCategoryData,
	detailLoading,
}) => {
	const [categoryName, setCategoryName] = useState("");
	const [isLoading, setLoading] = useState<boolean>(false);
	const [errors, setErrors] = useState<{
		categoryName?: string;
	}>({});

	const clearForm = () => {
		setCategoryName("");
		setErrors({});
		setLoading(false);
	};

	useEffect(() => {
		setLoading(false);
	}, [detailLoading]);

	React.useEffect(() => {
		if (
			id > 0 &&
			videoHotlineCategoryData?.items?.eventCategories?.length > 0
		) {
			const selectedCategory =
				videoHotlineCategoryData.items.eventCategories.find(
					(cat: any) => cat.eventCategoryId === id
				);
			if (selectedCategory) {
				setCategoryName(selectedCategory.categoryName ?? "");
			}
		} else {
			clearForm();
		}
	}, [id]);

	useEffect(() => {
		if (
			!videoHotlineCategoryData.isSuccess &&
			videoHotlineCategoryData.isError
		) {
			setLoading(false);
		}
	}, [videoHotlineCategoryData.isError]);

	React.useEffect(() => {
		if (videoHotlineCategoryData.isSuccessSave === true) {
			clearForm();
			toggleDetailModal();
			setLoading(false);	
			// videoHotlineCategoryData.isSuccessSave = false;
		}
		if(videoHotlineCategoryData.isError === true) {
			setLoading(false);
			videoHotlineCategoryData.isError = false;
		}
	}, [videoHotlineCategoryData.isSuccessSave, videoHotlineCategoryData.isError]);

	const validate = () => {
		const newErrors: { categoryName?: string; description?: string } = {};

		if (!categoryName.trim()) {
			newErrors.categoryName = "Category name is required.";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (!validate()) return;
		setLoading(true);
		const payload = {
			eventCategory: {
				eventCategoryId: id !== 0 ? id : 0,
				categoryName: categoryName,
			},
		};
		try {
			if (id === 0) {
				await dispatch(
					VideoHotlineCategoryAction.videoHotlineCategoryCreate(payload)
				);
			} else {
				await dispatch(
					VideoHotlineCategoryAction.videoHotlineCategoryUpdate(payload)
				);
			}
			// clearForm();
		} catch (error) {
			console.error("Error while saving:", error);
			showAlert("Failed to save category.", true);
		} finally {
			// setLoading(false);
			// toggleDetailModal();
		}
	};

	return (
		<Modal isOpen={isOpen} size="lg" centered backdrop={true}>
			<ModalHeader>
				{id > 0 ? "Manage Category Details" : "Add New Category"}
			</ModalHeader>
			<Form onSubmit={handleSubmit}>
				<ModalBody>
					<FormGroup>
						<Label for="categoryName" className="col-form-label">
							Category Name
						</Label>
						<Input
							id="categoryName"
							type="text"
							placeholder="Enter category name"
							value={categoryName}
							onChange={(e) => {
								setCategoryName(e.target.value);
								setErrors((prev) => ({ ...prev, categoryName: undefined }));
							}}
							invalid={!!errors.categoryName}
							max={30}
							maxLength={30}
						/>
						{errors.categoryName && (
							<FormFeedback>{errors.categoryName}</FormFeedback>
						)}
						{categoryName?.length >= 30 && (
							<span className="text-warning">
								Character limit met. Maximum 30 characters allowed
							</span>
						)}
					</FormGroup>
				</ModalBody>

				<ModalFooter>
					<Button
						type="button"
						color="secondary"
						disabled={isLoading}
						onClick={() => {
							toggleDetailModal();
							clearForm();
						}}
					>
						Cancel
					</Button>
					<Button type="submit" color="primary" disabled={isLoading}>
						{isLoading && <Spinner size="sm" />} {id > 0 ? "Update" : "Add"}
					</Button>
				</ModalFooter>
			</Form>
		</Modal>
	);
};

export default VideoHotlineCategoryModal;
